import { ThemeService } from './../../../../core/services/theme.service';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { firstValueFrom, of, switchMap, take, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { parentListPath } from '../../patient-file/patient-file-parent';
import { GroupService } from './../../../../core/services/group.service';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { ScheduleService } from './../../../../core/services/schedule.service';
import {
  describeRecurrence,
  occurrenceFormDates,
  Recurrence,
  recurrenceFromForm,
  recurrenceToForm,
  WEEKDAY_CODES,
  WEEKDAY_SHORT,
} from 'src/app/core/utils/schedule-events';
import { EventScope, EventScopeDialogComponent } from '../event-scope-dialog/event-scope-dialog.component';

/** A one-day timed event must end after it starts; over several days any times are fine. */
function eventTimesValid(group: AbstractControl): ValidationErrors | null {
  const { startDate, endDate, startTime, endTime, isAllDay } = group.value;
  if (startDate && endDate && moment(endDate).isBefore(moment(startDate), 'day')) return { invalidEndDate: true };
  const sameDay = startDate && endDate && moment(endDate).isSame(moment(startDate), 'day');
  if (!isAllDay && sameDay && startTime && endTime && moment(endTime, 'HH:mm').isSameOrBefore(moment(startTime, 'HH:mm'))) {
    return { invalidEndTime: true };
  }
  return null;
}

@Component({
  selector: 'app-event-detail',
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.scss']
})
export class EventDetailComponent extends BaseComponent implements OnInit {
  todayDate: moment.Moment = moment();

  form!: FormGroup;
  editingEvent: any;
  groupId!: string;
  /** The occurrence of a recurring event that was opened ('YYYY-MM-DD'), if any. */
  occurrenceDate: string | null = null;
  calendarSplitByPhysician = false;
  physicians: any[] = [];

  readonly weekdays = WEEKDAY_CODES.map(code => ({ code, label: WEEKDAY_SHORT[code] }));

  darkTheme!: NgxMaterialTimepickerTheme;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private scheduleService: ScheduleService,
    private appointmentService: PatientAppointmentService,
    private cd: ChangeDetectorRef,
    private themeService: ThemeService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    this.route.paramMap.pipe(
      takeUntil(this.destroy$),
      switchMap(params => {
        const id = params.get('id');
        return id ? this.scheduleService.getEventById(id) : of(null);
      }),
    ).subscribe(event => {
      if (event && event.data) {
        this.editingEvent = event.data;
        this.occurrenceDate = this.route.snapshot.queryParamMap.get('occurrence');
        this.patchForm(event.data);
      } else {
        this.resetForm();

        this.route.queryParams
          .pipe(takeUntil(this.destroy$), take(1))
          .subscribe(params => {
            const date = params['date'];
            if (date) {
              setTimeout(() => {
                const providedDate = moment(date, 'YYYY-MM-DD');
                this.form.patchValue({ startDate: providedDate, endDate: providedDate });
                this.form.get('repeat')?.patchValue(recurrenceToForm(null, providedDate));
              });
            }
          });
      }
    });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.calendarSplitByPhysician = !!selectedGroup.calendarSplitByPhysician;
          this.form.patchValue({ groupId: this.groupId });
        }
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.physicians = (users || []).filter(
          (u: any) => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50',
        );
      });

    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  initForm() {
    this.form = this.fb.group({
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      reason: ['', Validators.required],
      virtualLink: [''],
      textColor: ['#ffffff'],
      backgroundColor: ['#f44336'],
      isAllDay: [false],
      showAsBusy: [false],
      physicianId: [''],
      groupId: [this.groupId, Validators.required],
      repeat: this.fb.group(recurrenceToForm(null, moment())),
    }, {
      validators: [eventTimesValid]
    });
  }

  /** True when the event being edited repeats (it was saved with a rule). */
  get isSeries(): boolean {
    return !!this.editingEvent?.recurrence;
  }

  get repeatChoice(): string {
    return this.form.get('repeat.repeat')?.value;
  }

  get repeatEnds(): string {
    return this.form.get('repeat.ends')?.value;
  }

  get repeatUnit(): string {
    const unit = { daily: 'day', weekly: 'week', monthly: 'month' }[this.repeatChoice] || '';
    return Number(this.form.get('repeat.interval')?.value) > 1 ? `${unit}s` : unit;
  }

  get repeatSummary(): string {
    return describeRecurrence(this.currentRecurrence(), this.form.get('startDate')?.value);
  }

  setDefaultEndDate(event: MatDatepickerInputEvent<Date>) {
    const start = moment(event.value);
    const end = this.form.get('endDate')?.value;
    if (!end || moment(end).isBefore(start, 'day')) {
      this.form.patchValue({ endDate: event.value });
    }
    if (!this.isSeries && this.repeatChoice === 'none') {
      this.form.get('repeat.weekdays')?.setValue(recurrenceToForm(null, start).weekdays);
    }
  }

  toggleAllDay(event: MatSlideToggleChange) {
    if (event.checked) {
      this.form.patchValue({ startTime: '00:00', endTime: '23:59' });
    }
  }

  patchForm(event: any) {
    if (!event) {
      return;
    }
    const dates = this.occurrenceDate && event.recurrence
      ? occurrenceFormDates(event, this.occurrenceDate)
      : { startDate: moment(event.startDate), endDate: moment(event.endDate || event.startDate) };
    this.form.patchValue({
      ...event,
      ...dates,
      showAsBusy: event.showAsBusy === true,
      physicianId: event.physicianId || '',
    });
    this.form.get('repeat')?.patchValue(recurrenceToForm(event.recurrence, event.startDate));
  }

  resetForm() {
    setTimeout(() => {
      this.editingEvent = null;
      this.occurrenceDate = null;
      this.form.reset();
      this.initForm();
      this.form.patchValue({ groupId: this.groupId });
      this.cd.detectChanges();
    });
  }

  private currentRecurrence(): Recurrence | null {
    return recurrenceFromForm(this.form.get('repeat')?.value, this.form.get('startDate')?.value);
  }

  /** What is sent to the server: plain calendar days and the repeat rule, without the repeat fields. */
  private eventPayload(): any {
    const { repeat, ...value } = this.form.value;
    return {
      ...value,
      startDate: moment(value.startDate).format('YYYY-MM-DD'),
      endDate: moment(value.endDate || value.startDate).format('YYYY-MM-DD'),
      physicianId: this.calendarSplitByPhysician ? (value.physicianId || null) : (this.editingEvent?.physicianId ?? null),
      recurrence: this.currentRecurrence(),
    };
  }

  /** For a recurring event opened on one occurrence: which occurrences to change. Undefined = cancelled. */
  private async askScope(action: 'save' | 'delete'): Promise<EventScope | undefined> {
    if (!this.isSeries || !this.occurrenceDate) return 'all';
    return firstValueFrom(this.dialog.open(EventScopeDialogComponent, { width: '340px', data: { action } }).afterClosed());
  }

  async saveEvent() {
    const payload = this.eventPayload();
    const id = this.editingEvent?.id || '';
    // kind=event: an event without a physician clashes with every physician's bookings on a split calendar.
    const appointmentsResponse = await firstValueFrom(this.appointmentService.checkAppointmentSlotAvailability(
      payload.startDate, payload.endDate, payload.startTime, payload.endTime, this.groupId, id,
      payload.physicianId, 'event', { isAllDay: payload.isAllDay, recurrence: payload.recurrence },
    ));
    const appointments = appointmentsResponse.data;

    if (appointments.length >= 1) {
      const proceedWithBooking = await this.showConfirmationDialog('SCHEDULING CONFLICT', 'Appointments/Events already exist for this slot. Do you want to continue anyway?', appointments);
      if (!proceedWithBooking) return;
    }

    if (this.editingEvent?.id) {
      const scope = await this.askScope('save');
      if (!scope) return;
      this.scheduleService.updateEvent(this.editingEvent.id, { ...payload, scope, occurrenceDate: this.occurrenceDate })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showToastMessage('Event updated successfully');
            this.returnToParent();
          },
          error: (error) => {
            console.error('Error updating event: ', error);
            this.showToastMessage('There was an error updating the event. Please try again.');
          }
        });
    } else {
      this.scheduleService.addEvent(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showToastMessage('Event saved successfully');
            this.returnToParent();
          },
          error: (error) => {
            console.error('Error saving event: ', error);
            this.showToastMessage('There was an error saving the event. Please try again.');
          }
        });
    }
  }

  async deleteEvent() {
    const scope = this.isSeries && this.occurrenceDate ? await this.askScope('delete') : (await this.showConfirmationDialog() ? 'all' : undefined);
    if (!scope) return;

    this.scheduleService.deleteEvent(this.editingEvent.id, this.isSeries ? scope : undefined, this.occurrenceDate ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage('Event deleted successfully');
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error deleting the event: ', error);
          this.showToastMessage('There was an error deleting the event. Please try again.');
        }
      });
  }

  /** Back to the page the form was opened over: the calendar, the Connect page or a list. */
  returnToParent() {
    const parentPath = parentListPath(this.router.url);
    this.router.navigate(['/patients', { outlets: { patient: null } }]).then(() => {
      this.router.navigate(['/patients', parentPath]);
    });
  }

}
